import { AsksQuery, BidsQuery, TokenActivityQuery, useBids, useDynamicTokens, useListings, UserTokenQuery, useTokenActivity, useUserTokens } from '@reservoir0x/reservoir-kit-ui'
import { useMemo } from 'react'
import { getContractAddressesForSystem } from 'config/routes'
import { getSystemIdChains } from 'utils/multichain'
import { sortTokenFloorListings } from 'utils/mergeChainV2'
import { useAuthWallets } from './auth/useAuthWallets'

/*
  This custom hook is used to merge the data from multiple chains for a given token.
  It returns a single hook instance with the merged data, error, and loading state.
*/

type TokenActivity = NonNullable<ReturnType<typeof useTokenActivity>['data']>[0]

type MutateReturn<T> = T extends 'activity'
  ? ReturnType<ReturnType<typeof useTokenActivity>['mutate']>
  : T extends 'listings'
  ? ReturnType<ReturnType<typeof useListings>['mutate']>
  : T extends 'offers'
  ? ReturnType<ReturnType<typeof useBids>['mutate']>
  : T extends 'user'
  ? ReturnType<ReturnType<typeof useUserTokens>['mutate']>
  : T extends 'dynamic'
  ? ReturnType<ReturnType<typeof useDynamicTokens>['mutate']>
  : never;

type ListingsResponse = ReturnType<typeof useListings>['data'];
type ActivityResponse = NonNullable<ReturnType<typeof useTokenActivity>['data']>;
type OffersResponse = NonNullable<ReturnType<typeof useBids>['data']>;
type UserTokenResponse = NonNullable<ReturnType<typeof useUserTokens>['data']>;
type DynamicTokenResponse = NonNullable<ReturnType<typeof useDynamicTokens>['data']>;
type MergedDataReturn<T> = {
  data: T extends 'activity' ? ActivityResponse :
  T extends 'listings' ? ListingsResponse :
  T extends 'offers' ? OffersResponse :
  T extends 'user' ? UserTokenResponse :
  T extends 'dynamic' ? DynamicTokenResponse :
  never;
  error: Error | null;
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => Promise<MutateReturn<T>>;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingInitialData: boolean;
  isFetchingPage: boolean;
  resetCache: () => Promise<void>;
  size: number;
  setSize: (size: number | ((_size: number) => number)) => Promise<any>;
}

export const useMergedTokenData = <T extends 'activity' | 'listings' | 'offers' | 'user' | 'dynamic'>(
  systemId: string,
  tokenId: string,
  type: T,
  options: TokenActivityQuery | AsksQuery | BidsQuery | UserTokenQuery,
): MergedDataReturn<T> => {
  const { ownershipWallet } = useAuthWallets()
  const contractAddresses = getContractAddressesForSystem(systemId)
  const systemIdChains = getSystemIdChains(systemId).supportedSystemIdChains

  const validTypes = ['activity', 'listings', 'offers', 'user', 'dynamic'] as const;
  if (!validTypes.includes(type)) {
    throw new Error('Invalid type specified');
  }

  // Create a hook instance for each contract based on the type of data we want to fetch
  const dataResults = contractAddresses.map((contract, contractIndex) => {
    const listingsBidsQuery: Parameters<typeof useListings>['0'] = {
      token: `${contract}:${tokenId ?? ''.toString()}`,
      includeCriteriaMetadata: true,
      includeRawData: true,
      sortBy: 'price',
      status: 'active',
    }

    // Ensure the correct chainId is used for each contract
    const chainId = systemIdChains[contractIndex]?.id

    switch (type) {
      case 'activity':
        return useTokenActivity(`${contract}:${tokenId}`, options as TokenActivityQuery, {}, chainId);
      case 'listings':
        return useListings(
          listingsBidsQuery,
          { revalidateFirstPage: true },
          true,
          chainId
        );
      case 'offers':
        return useBids(
          listingsBidsQuery,
          { revalidateFirstPage: true },
          true,
          chainId
        );
      case 'user':
        return useUserTokens(
          ownershipWallet ? ownershipWallet.address : undefined,
          { tokens: [`${contract}:${tokenId}`], ...options } as UserTokenQuery,
          {
            fallbackData: [],
          },
          chainId
        );
      case 'dynamic':
        return useDynamicTokens(
          {
            limit: 1,
            includeQuantity: true,
            includeDynamicPricing: true,
            includeAttributes: true,
            includeLastSale: true,
            includeTopBid: true,
            tokens: [`${contract}:${tokenId}`],
          },
          {},
          chainId
        );
      default:
        throw new Error('Invalid type specified');
    }
  });

  const firstResult = dataResults[0] // Use first result for base properties

  return {
    data: useMemo(() => {
      if (type === 'listings') {
        const listings = dataResults.flatMap(result =>
          (result.data || []) as ListingsResponse
        );

        return sortTokenFloorListings(listings);
      }

      if (type === 'activity') {
        const activities = dataResults.flatMap(result =>
          (result.data || []) as TokenActivity[]
        ).sort((a, b) =>
          (new Date(b.timestamp || 0).getTime()) - (new Date(a.timestamp || 0).getTime())
        );

        return activities;
      }

      // Offers
      if (type === 'offers') {
        const offers = dataResults.flatMap(result =>
          (result.data || []) as OffersResponse
        ).sort((a, b) =>
          // Sort by offer amount, highest first
          (Number(b.price?.amount?.usd) || 0) - (Number(a.price?.amount?.usd) || 0)
        );

        return offers;
      }

      if (type === 'user') {
        const userTokens = dataResults.flatMap(result =>
          (result.data || []) as UserTokenResponse
        );

        return userTokens;
      }

      if (type === 'dynamic') {
        const dynamicTokens = dataResults.flatMap(result =>
          (result.data || []) as DynamicTokenResponse
        );

        return dynamicTokens;
      }
    }, [dataResults, type]) as MergedDataReturn<T>['data'],
    error: firstResult?.error,
    isLoading: dataResults.some(result => result.isLoading),
    isValidating: dataResults.some(result => result.isValidating),
    mutate: async () => {
      const results = await Promise.all(
        dataResults.map(result => result.mutate())
      )

      return (results[0] as unknown) as MutateReturn<T>
    },
    fetchNextPage: () => dataResults.forEach(result => result.fetchNextPage()),
    hasNextPage: firstResult?.hasNextPage,
    isFetchingInitialData: firstResult?.isFetchingInitialData,
    isFetchingPage: firstResult?.isFetchingPage,
    resetCache: async () => {
      await Promise.all(
        dataResults.map(result => result.resetCache())
      )
    },
    size: firstResult?.size,
    setSize: async (size) => {
      const results = await Promise.all(
        dataResults.map(result => {
          if (typeof result.setSize === 'function') {
            return result.setSize(size);
          }
          throw new Error('setSize is not a function on one of the results');
        })
      )
      return results[0]
    }
  }
} 
